import {
  ApiStatus,
  ISharedWithUser,
  ReportSharingSettings,
  ResourceSharingSettings,
  SharingSettingsTypes,
} from '../../models';
import {ReportFormActionTypes, UnsetReportAction} from '../actions/reportFormActions';
import {
  ILoadedSharingSettingsAction,
  ISharedWithGroupAction,
  ISharedWithUserAction,
  ISharedWithWorkspaceAction,
  IUnsetSharingSettingsAction,
  SharingSettingsActions,
  SharingSettingsActionsTypes,
} from '../actions/sharingSettingsActions';

export const initialSharingSettingsState: ISharingSettingsState = {
  loadingStatus: null,
  loadingSelected: ApiStatus.LOADED,
  selectedSharingSettings: null,
  reportSharingSettings: null,
}

export type SelectedSharingSettings = ResourceSharingSettings | ReportSharingSettings;

export interface ISharingSettingsState {
  loadingStatus: ApiStatus | null;
  loadingSelected: ApiStatus;
  reportSharingSettings: ReportSharingSettings | null;
  selectedSharingSettings: SelectedSharingSettings[] | null; // List of Sharing settings (can be mixed)
}

/*
 * A reducer for handling Sharing Settings for only one entity.
 */
export default function sharingSettingsReducer(
  state: ISharingSettingsState = initialSharingSettingsState,
  action: SharingSettingsActions | UnsetReportAction
): ISharingSettingsState {
  switch (action.type) {
    case SharingSettingsActionsTypes.LOAD_SHARING_SETTINGS:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }
    case SharingSettingsActionsTypes.LOADING_SHARING_SETTINGS_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }
    case SharingSettingsActionsTypes.LOAD_SELECTED_SHARING_SETTINGS:
      return {
        ...state,
        loadingSelected: ApiStatus.LOADING,
      }
    case SharingSettingsActionsTypes.LOADED_SELECTED_SHARING_SETTINGS:
      return {
        ...state,
        selectedSharingSettings: action.payload,
        loadingSelected: ApiStatus.LOADED,
      }
    case SharingSettingsActionsTypes.LOADED_SHARING_SETTINGS:
      return handleLoadedSharingSettings(state, action);
    case SharingSettingsActionsTypes.UNSET_SHARING_SETTINGS:
      return handleUnsetSharingSettings(state, action);
    case SharingSettingsActionsTypes.SHARED_WITH_USER:
      return handleShareWithUser(state, action);
    case SharingSettingsActionsTypes.SHARED_WITH_WORKSPACE:
      return handleShareWithWorkspace(state, action);
    case SharingSettingsActionsTypes.SHARED_WITH_GROUP:
      return handleShareWithGroup(state, action);
    case ReportFormActionTypes.UNSET_REPORT:
      return {
        ...state,
        reportSharingSettings: null,
      }
    default:
      return state;
  }
}

// Functions

function handleLoadedSharingSettings(
  state: ISharingSettingsState,
  action: ILoadedSharingSettingsAction
): ISharingSettingsState {
  let stateChunk: Partial<ISharingSettingsState> = {};
  if (action.payload.type === SharingSettingsTypes.REPORT) {
    stateChunk = {
      reportSharingSettings: {
        ...action.payload.settings,
        uid: action.payload.uid,
      },
    }
  }

  return {
    ...state,
    ...stateChunk,
    loadingStatus: ApiStatus.LOADED,
  }
}

function handleUnsetSharingSettings(
  state: ISharingSettingsState,
  action: IUnsetSharingSettingsAction,
): ISharingSettingsState {
  const isReport = action.payload === SharingSettingsTypes.REPORT;
  // const isDatabase = action.payload === SharingSettingsTypes.DATABASE;

  return {
    ...state,
    reportSharingSettings: isReport ? null : state.reportSharingSettings,
  }
}

function _handleSharedWith(currentSharingSettings: any[], newValue: any) {
  /*
   * Used for shared with User or Group
   */
  const item = currentSharingSettings.find((value: any) => value.uid === newValue.uid);

  let newSharingSettings: any[] = [];
  if (item) {
    // if item is found, check if user was not removed from sharing settings
    if (!(newValue.shared.edit || newValue.shared.view)) {
      // if both are false, it means user was removed
      newSharingSettings = currentSharingSettings.filter((value: any) => value.uid !== newValue.uid);
    } else {
      // update sharing settings
      newSharingSettings = currentSharingSettings.map((sharingSettings: ISharedWithUser) => {
        if (sharingSettings.uid === newValue.uid) {
          return {
            ...newValue.shared,
            uid: newValue.uid,
          }
        }
        return sharingSettings;
      });
    }
  } else {
    newSharingSettings = [
      ...currentSharingSettings,
      {
        ...newValue.shared,
        uid: newValue.uid,
      } // add new item
    ]
  }

  return newSharingSettings;
}

function handleShareWithUser(
  state: ISharingSettingsState,
  action: ISharedWithUserAction,
): ISharingSettingsState {
  let stateChunk: Partial<ISharingSettingsState> = {};

  if (action.payload.type === SharingSettingsTypes.REPORT) {
    let sharedWithUsers = state.reportSharingSettings?.shared_with_users || [];
    let newSharedWithUsers = _handleSharedWith(sharedWithUsers, action.payload.value);

    stateChunk = {
      reportSharingSettings: {
        ...state.reportSharingSettings as ReportSharingSettings,
        uid: action.payload.uid,
        shared_with_users: [
          ...newSharedWithUsers,
        ]
      },
    }
  }

  return {
    ...state,
    ...stateChunk,
    loadingStatus: ApiStatus.LOADED,
  }
}

function handleShareWithGroup(
  state: ISharingSettingsState,
  action: ISharedWithGroupAction,
): ISharingSettingsState {
  let stateChunk: Partial<ISharingSettingsState> = {};

  if (action.payload.type === SharingSettingsTypes.REPORT) {
    const key = 'reportSharingSettings';
    const existing: any = state.hasOwnProperty(key) ? (state as any)[key] : {};

    stateChunk = {
      [key]: {
        ...existing,
        shared_with_groups: _handleSharedWith(
          state.reportSharingSettings?.shared_with_groups || [],
          action.payload.value
        ),
      }
    }
  }

  return {
    ...state,
    ...stateChunk,
    loadingStatus: ApiStatus.LOADED,
  }
}

function handleShareWithWorkspace(
  state: ISharingSettingsState,
  action: ISharedWithWorkspaceAction,
): ISharingSettingsState {
  let stateChunk: Partial<ISharingSettingsState> = {};
  let key = null;
  if (action.payload.type === SharingSettingsTypes.REPORT) {
    key = 'reportSharingSettings';
  }

  if (key !== null) {
    const existing: any = state.hasOwnProperty(key) ? (state as any)[key] : {};

    stateChunk = {
      [key]: {
        ...existing,
        uid: action.payload.uid,
        shared_with_workspace: action.payload?.value.shared,
      },
    }
  }

  return {
    ...state,
    ...stateChunk,
    loadingStatus: ApiStatus.LOADED,
  }
}
